/** 后台未加载配置时的兜底值（与 DEFAULT_FOLDER_MAX_DEPTH 一致） */
export const DEFAULT_FOLDER_MAX_DEPTH = 8

export const INGESTION_FOLDER_NAME = '采集文件夹'

export const TREE_ROOT_KEY = 'all' as const

export type FolderRow = {
  id: number
  name: string
  parent_id: number | null
  sort_order: number
  is_ingestion_inbox?: boolean
}

export type FolderNode = FolderRow & { has_children: boolean }

export type FolderLabel = {
  id: number
  name: string
  parent_id: number | null
  ai_auto_summary?: boolean
  ai_auto_tags?: boolean
  is_ingestion_inbox?: boolean
}

export type TreeKey = typeof TREE_ROOT_KEY | `folder-${number}`

export function isIngestionInboxFolder(
  folder: Pick<FolderRow, 'name' | 'parent_id'> & { is_ingestion_inbox?: boolean },
): boolean {
  if (folder.is_ingestion_inbox) return true
  return folder.parent_id == null && folder.name === INGESTION_FOLDER_NAME
}

export function parseTreeKey(key: string): TreeKey {
  if (key === TREE_ROOT_KEY) return TREE_ROOT_KEY
  if (key.startsWith('folder-')) return key as `folder-${number}`
  return TREE_ROOT_KEY
}

export function folderIdFromKey(key: TreeKey): number | null {
  if (typeof key === 'string' && key.startsWith('folder-')) return Number(key.slice(7))
  return null
}

export function getFolderDepth(
  folderId: number,
  byId: Map<number, Pick<FolderRow, 'id' | 'parent_id'>>,
): number {
  let depth = 1
  let cur = byId.get(folderId)
  while (cur?.parent_id != null) {
    depth += 1
    cur = byId.get(cur.parent_id)
    if (depth > 64) break
  }
  return depth
}

export function expandKeysForTreeKey(_treeKey: TreeKey, ancestorIds: number[]): string[] {
  return ancestorIds.map((id) => `folder-${id}`)
}

export function expandKeysForSearchNodes(nodes: FolderNode[]): string[] {
  return nodes.map((f) => `folder-${f.id}`)
}

export function folderSelectOptions(
  folders: FolderLabel[],
  parentId: number | null = null,
  prefix = '',
): { value: number; label: string }[] {
  const out: { value: number; label: string }[] = []
  for (const f of folders.filter((x) => x.parent_id === parentId)) {
    const label = prefix ? `${prefix} / ${f.name}` : f.name
    out.push({ value: f.id, label })
    out.push(...folderSelectOptions(folders, f.id, label))
  }
  return out
}

export function folderPathLabel(
  folders: FolderLabel[],
  folderId: number | null | undefined,
): string {
  if (!folderId) return ''
  const byId = new Map(folders.map((f) => [f.id, f]))
  const parts: string[] = []
  let cur = byId.get(folderId)
  let guard = 0
  while (cur && guard < 64) {
    parts.unshift(cur.name)
    cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined
    guard += 1
  }
  return parts.join(' / ')
}

export type FolderTreeNode = {
  key: number
  title: string
  children?: FolderTreeNode[]
}

export function buildFolderTreeData(folders: FolderLabel[]): FolderTreeNode[] {
  const byParent = new Map<number | null, FolderLabel[]>()
  for (const f of folders) {
    const k = f.parent_id
    if (!byParent.has(k)) byParent.set(k, [])
    byParent.get(k)!.push(f)
  }
  const walk = (parentId: number | null): FolderTreeNode[] =>
    (byParent.get(parentId) || []).map((f) => {
      const children = walk(f.id)
      return {
        key: f.id,
        title: f.name,
        children: children.length ? children : undefined,
      }
    })
  return walk(null)
}
